import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import ListHeader from '@/common/components/ListHeader'
import { ModalErrorMessage } from '@/common/components/ModalErrorMessage'
import Spinner from '@/common/components/Spinner'
import TextArea from '@/common/components/TextArea'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import { BillingTransactionIdentifier } from '@/common/types/billing/transactionIdentifier.type'
import { ErrorResponse } from '@/common/types/errorResponse'
import { formatCurrency, formatDate } from '@/common/utils/formatters'
import ModalSuccess from '@/features/refund/components/ModalSuccess'
import { TransactionData } from '@/features/refund/components/transactionData'
import { useBillingTransactionIdentifiers } from '@/features/refund/hooks/useBillingTransactionIdentifiers'
import { useCreateRefund } from '@/features/refund/hooks/useCreateRefund'
import { createSchema, RefundValidationSchema } from '@/features/refund/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { SquareXIcon } from 'lucide-react'
import { Dispatch, SetStateAction, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

const Create = () => {
  const [debouncedTransactionIdentifier, setDebouncedTransactionData] = useDebounce('')

  const [modalSuccessData, setModalSuccessData] = useState<BillingTransactionIdentifier | boolean>(false)
  const [modalErrorData, setModalErrorData] = useState<ErrorResponse | boolean>(false)

  const {
    data: transactionIdentifiers,
    isLoading: transactionIdentifiersIsLoading,
    error: transactionIdentifiersError,
  } = useBillingTransactionIdentifiers<BillingTransactionIdentifier>({
    identifier: debouncedTransactionIdentifier || '',
  })

  const {
    register,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
  } = useForm<RefundValidationSchema>({
    resolver: zodResolver(createSchema),
  })

  const { mutate: createRefund, isPending: createRefundIsPending } = useCreateRefund({
    onSuccess: (response) => {
      setModalSuccessData(response.data.data)
    },
    onError: (response) => {
      setModalErrorData(response)
    },
  })

  const onSubmit: SubmitHandler<RefundValidationSchema> = (data) => createRefund(data)

  const clearForm = () => {
    resetForm()
    setDebouncedTransactionData('')
  }

  return (
    <>
      <form className="flex flex-col gap-6 h-full" onSubmit={handleSubmit(onSubmit)}>
        <ListHeader Icon={<SquareXIcon className="size-10" strokeWidth={1.5} />} title="Estorno" />

        <Box className="flex flex-col gap-6 grow h-[calc((100vh-276px)/2)] overflow-y-auto">
          <div className="flex gap-6">
            <div className="w-full h-full">
              <div className="flex flex-col gap-6 h-full">
                <p className="text-[20px] font-bold">Confirmações de segurança</p>

                <TextField
                  label="N. da Transação"
                  data-testid="transaction_identifier_input"
                  errorMessage={
                    errors?.transaction_identifier?.message ||
                    transactionIdentifiersError?.response?.data?.errors?.base?.[0] ||
                    undefined
                  }
                  {...register('transaction_identifier')}
                  onChange={(e) => {
                    setDebouncedTransactionData((e.target as HTMLInputElement).value)
                    register('transaction_identifier').onChange(e)
                  }}
                  isLoading={transactionIdentifiersIsLoading}
                  disabled={createRefundIsPending}
                />

                <div className="w-full space-y-4">
                  <p>Dados para estorno</p>
                  <TextArea
                    data-testid="reason_input"
                    placeholder="Motivo do estorno"
                    {...register('reason')}
                    errorMessage={errors?.reason?.message}
                    disabled={createRefundIsPending}
                  />
                </div>
              </div>
            </div>

            <div className="flex w-full flex-col gap-6">
             {transactionIdentifiers.id && (
              <>
              <div>
                <p className="font-bold text-xl">Resumo da transação</p>
                <p className="italic font-light text-sm text-gray-300">Data da transação:
                 {" "} {formatDate(new Date(transactionIdentifiers?.eventable?.created_at))}</p>
              </div>

              <TransactionData transactionIdentifiers={transactionIdentifiers} />
              </>
             )}
            </div>
          </div>

          <div className="flex gap-4 self-end mt-auto">
            <div className="flex flex-col items-end">
              <p className="font-light text-sm italic leading-4">Valor total</p>
              <p className="font-bold text-lg leading-5">
                {formatCurrency(transactionIdentifiers?.eventable?.total_amount || 0)}
              </p>
            </div>
            <Button
              data-testid="confirm_refund"
              type="submit"
              variant="error"
              disabled={!transactionIdentifiers.id || createRefundIsPending}
            >
              {createRefundIsPending ? <Spinner className="h-6 mx-[23px] fill-white-800" /> : 'Estornar transação'}
            </Button>
          </div>
        </Box>
      </form>

      <ModalSuccess
        data={modalSuccessData as BillingTransactionIdentifier}
        setData={setModalSuccessData}
        clearForm={clearForm}
      />
      <ModalErrorMessage
        data={modalErrorData as ErrorResponse}
        setData={setModalErrorData as Dispatch<SetStateAction<boolean>>}
        title="Estorno não concluído"
        message="Algo deu errado e seu estorno não foi concluído."
      />
    </>
  )
}

export default Create
